import { useState, useCallback } from 'react';
import {
  callOpenAI,
  callAnthropic,
  callCustomEndpoint
} from '../../utils/llm-generator/apiClients';
import {
  buildRerollPrompt,
  buildVariationPrompt,
  buildRephrasingPrompt
} from '../../utils/llm-generator/promptBuilder';
import {
  parseQuestionResponse,
  toggleMultipleChoice as toggleMultipleChoiceUtil
} from '../../utils/llm-generator/questionTransformers';
import { Logger } from '../../utils/logger';

const log = Logger.create('useQuestionOperations');

/**
 * Custom Hook für Fragen-Operationen (Reroll, Variation, Shuffle etc.)
 * 
 * Verwaltet:
 * - Reroll (komplett neue Frage generieren)
 * - Variation (ähnliche Frage hinzufügen)
 * - Rephrase (Umformulieren)
 * - Toggle Multiple Choice
 * - Shuffle Answers
 */
export function useQuestionOperations(config, questions, setQuestions) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingIndex, setProcessingIndex] = useState(null);
  
  /**
   * Make LLM API call based on provider
   */
  const callLLM = useCallback(async (systemPrompt, userPrompt) => {
    const {
      llmProvider,
      apiKey,
      model,
      baseUrl,
      temperature,
      maxTokens
    } = config;
    
    if (llmProvider === 'openai' || llmProvider === 'groq' || llmProvider === 'openrouter') {
      return await callOpenAI(systemPrompt, userPrompt, apiKey, model, baseUrl, temperature, maxTokens);
    } else if (llmProvider === 'anthropic') {
      return await callAnthropic(systemPrompt, userPrompt, apiKey, model, baseUrl, temperature, maxTokens);
    } else {
      return await callCustomEndpoint(systemPrompt, userPrompt, baseUrl, apiKey, model, temperature, maxTokens);
    }
  }, [config]);
  
  /**
   * Reroll a question (replace with completely new one)
   */
  const rerollQuestion = useCallback(async (index, options = {}) => {
    const {
      includeCodeBlocks = true,
      useConversationHistory = false
    } = options;
    
    const { llmProvider, apiKey, systemPrompt } = config;
    
    if (!apiKey && llmProvider !== 'custom') {
      return { success: false, error: 'Bitte API-Key in den Einstellungen eingeben' };
    }
    
    setIsProcessing(true);
    setProcessingIndex(index);
    
    try {
      const question = questions[index];
      const rerollInstruction = buildRerollPrompt(question, {
        includeCodeBlocks,
        allQuestions: useConversationHistory ? questions : [],
        questionIndex: index
      });
      
      log.debug(`Reroll Frage ${index + 1}, includeCodeBlocks=${includeCodeBlocks}, mitKontext=${useConversationHistory}`);
      
      const response = await callLLM(systemPrompt, rerollInstruction);
      const newQuestions = parseQuestionResponse(response);
      
      if (newQuestions.length > 0) {
        setQuestions(prev => {
          const updated = [...prev];
          updated[index] = newQuestions[0];
          return updated;
        });
        
        setIsProcessing(false);
        setProcessingIndex(null);
        return { success: true, question: newQuestions[0] };
      }
      
      throw new Error('Keine gültige Frage in der Antwort');
      
    } catch (err) {
      setIsProcessing(false);
      setProcessingIndex(null);
      return { success: false, error: `Fehler beim Reroll: ${err.message}` };
    }
  }, [config, questions, setQuestions, callLLM]);
  
  /**
   * Generate a variation of a question (add as new)
   */
  const generateVariation = useCallback(async (index, options = {}) => {
    const {
      includeCodeBlocks = true,
      useConversationHistory = false
    } = options;
    
    const { llmProvider, apiKey, systemPrompt } = config;
    
    if (!apiKey && llmProvider !== 'custom') {
      return { success: false, error: 'Bitte API-Key in den Einstellungen eingeben' };
    }
    
    setIsProcessing(true);
    setProcessingIndex(index);
    
    try {
      const question = questions[index];
      const variationInstruction = buildVariationPrompt(question, {
        includeCodeBlocks,
        allQuestions: useConversationHistory ? questions : [],
        questionIndex: index
      });
      
      log.debug(`Variation Frage ${index + 1}, includeCodeBlocks=${includeCodeBlocks}, mitKontext=${useConversationHistory}`);
      
      const response = await callLLM(systemPrompt, variationInstruction);
      const newQuestions = parseQuestionResponse(response);
      
      if (newQuestions.length > 0) {
        setQuestions(prev => [...prev, newQuestions[0]]);
        
        setIsProcessing(false);
        setProcessingIndex(null);
        return { success: true, question: newQuestions[0] };
      }
      
      throw new Error('Keine gültige Frage in der Antwort');
      
    } catch (err) {
      setIsProcessing(false);
      setProcessingIndex(null);
      return { success: false, error: `Fehler bei der Variation: ${err.message}` };
    }
  }, [config, questions, setQuestions, callLLM]);
  
  /**
   * Rephrase a question (keep concept, change wording)
   */
  const rephraseQuestion = useCallback(async (index, options = {}) => {
    const {
      includeCodeBlocks = true,
      useConversationHistory = false
    } = options;
    
    const { llmProvider, apiKey, systemPrompt } = config;
    
    if (!apiKey && llmProvider !== 'custom') {
      return { success: false, error: 'Bitte API-Key in den Einstellungen eingeben' };
    }
    
    setIsProcessing(true);
    setProcessingIndex(index);
    
    try {
      const question = questions[index];
      const rephraseInstruction = buildRephrasingPrompt(question, {
        includeCodeBlocks,
        allQuestions: useConversationHistory ? questions : [],
        questionIndex: index
      });
      
      log.debug(`Umformulieren Frage ${index + 1}, includeCodeBlocks=${includeCodeBlocks}, mitKontext=${useConversationHistory}`);
      
      const response = await callLLM(systemPrompt, rephraseInstruction);
      const newQuestions = parseQuestionResponse(response);
      
      if (newQuestions.length > 0) {
        setQuestions(prev => {
          const updated = [...prev];
          updated[index] = newQuestions[0];
          return updated;
        });
        
        setIsProcessing(false);
        setProcessingIndex(null);
        return { success: true, question: newQuestions[0] };
      }
      
      throw new Error('Keine gültige Frage in der Antwort');
      
    } catch (err) {
      setIsProcessing(false);
      setProcessingIndex(null);
      return { success: false, error: `Fehler beim Umformulieren: ${err.message}` };
    }
  }, [config, questions, setQuestions, callLLM]);
  
  /**
   * Toggle between single and multiple choice
   */
  const toggleMultipleChoice = useCallback((index) => {
    setQuestions(prev => {
      const updated = [...prev];
      updated[index] = toggleMultipleChoiceUtil(updated[index]);
      return updated;
    });
  }, [setQuestions]);
  
  /**
   * Shuffle answers for a question (Fisher-Yates)
   */
  const shuffleAnswers = useCallback((index) => {
    setQuestions(prev => {
      const updated = [...prev];
      const question = { ...updated[index] };
      const options = [...question.options];
      
      // Get current correct indices
      const currentCorrectIndices = question.correctAnswerIndices || [0];
      
      // Create array of indices
      const indices = options.map((_, i) => i);
      
      // Fisher-Yates shuffle
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }
      
      // Reorder options
      const shuffledOptions = indices.map(i => options[i]);
      
      // Map old correct indices to new positions
      const newCorrectIndices = currentCorrectIndices.map(oldIdx =>
        indices.indexOf(oldIdx)
      );
      
      question.options = shuffledOptions;
      question.correctAnswerIndices = newCorrectIndices;
      updated[index] = question;
      return updated;
    });
  }, [setQuestions]);
  
  /**
   * Delete a question
   */
  const deleteQuestion = useCallback((index) => {
    setQuestions(prev => prev.filter((_, i) => i !== index));
  }, [setQuestions]);
  
  /**
   * Move question up in list
   */
  const moveQuestionUp = useCallback((index) => {
    if (index === 0) return;
    setQuestions(prev => {
      const updated = [...prev];
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      return updated;
    });
  }, [setQuestions]);
  
  /**
   * Move question down in list
   */
  const moveQuestionDown = useCallback((index) => {
    setQuestions(prev => {
      if (index >= prev.length - 1) return prev;
      const updated = [...prev];
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      return updated;
    });
  }, [setQuestions]);
  
  return {
    // State
    isProcessing,
    processingIndex,
    
    // LLM Operations
    rerollQuestion,
    generateVariation,
    rephraseQuestion,
    
    // Local Operations
    toggleMultipleChoice,
    shuffleAnswers,
    deleteQuestion,
    moveQuestionUp,
    moveQuestionDown,
    
    // Utilities
    isProcessingQuestion: (index) => isProcessing && processingIndex === index
  };
}

export default useQuestionOperations;
